import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Container,
  Image,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { pxToRem } from "../utils/editor.ts";
import { PaymentService } from "../services/paymentService";

const PaymentPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState<string>('');
  const bgGradient = useColorModeValue(
    "linear(to-br, #e6f3ff, #f0f8ff, #e6f3ff)",
    "gray.800"
  );

  useEffect(() => {
    // 定时器轮询查询支付状态
    const pollPaymentStatus = async () => {
      try {
        const result = await PaymentService.checkPaymentStatus();
        setStatusMessage(result.message || '');
        
        // 根据支付状态跳转到相应页面
        if (result.status === 'completed') {
          navigate('/payment-success');
        } else if (result.status === 'failed' || result.status === 'cancelled') {
          navigate('/payment-failure');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        // 可以选择在错误时跳转到失败页面
        // navigate('/payment-failure');
      }
    };

    // 立即执行一次
    pollPaymentStatus();

    // 设置定时器，每1秒轮询一次
    const interval = setInterval(pollPaymentStatus, 1000);

    // 清理定时器
    return () => {
      clearInterval(interval);
    };
  }, [ navigate]);

  return (
    <Box
      bgGradient={bgGradient}
      h="100%"
      padding={pxToRem(30)}
      pt={0}
      overflowY={"auto"}
      display="flex"
      flexDirection="column"
      position="relative"
    >
      {/* Decorative background elements */}
      <Box
        position="absolute"
        top="20%"
        left="10%"
        w={pxToRem(200)}
        h={pxToRem(200)}
        bg="rgba(29, 138, 255, 0.1)"
        borderRadius="full"
        filter={`blur(${pxToRem(60)})`}
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom="20%"
        right="10%"
        w={pxToRem(300)}
        h={pxToRem(300)}
        bg="rgba(29, 138, 255, 0.08)"
        borderRadius="full"
        filter={`blur(${pxToRem(80)})`}
        zIndex={0}
      />

      {/* Header with user menu */}
      <Header />

      {/* Main content */}
      <Flex flex="1" align="center" justify="center" zIndex={1}>
        <Container maxW={pxToRem(1052)}>
          {/* Title Section */}
          <VStack
            spacing={pxToRem(32)}
            textAlign="center"
            mb={pxToRem(30)}
            alignItems="center"
          >
            <Heading
              as="h1"
              fontSize={pxToRem(32)}
              fontWeight="bold"
              color="gray.700"
            >
              From intelligent PDF reading to seamless writing and typesetting,
              streamline your journey from ideas to
              <span style={{ color: "#1D8AFF" }}>polished results</span>
            </Heading>
          </VStack>

          {/* Payment Pending Card */}
          <Box
            borderRadius={pxToRem(20)}
            pt={pxToRem(40)}
            pb={pxToRem(40)}
            paddingX={pxToRem(30)}
            boxShadow={`0 ${pxToRem(10)} ${pxToRem(40)} rgba(0, 0, 0, 0.1)`}
            maxW={pxToRem(998)}
            mx="auto"
            border="1px solid #D3D7E2"
            bg="#F5F6FA"
          >
            <VStack spacing={pxToRem(20)} alignItems="center">
              {/* Loading Animation */}
              <Box
                w={pxToRem(200)}
                h={pxToRem(200)}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  src="/static/loading-animate.svg"
                  alt="Loading"
                  w="100%"
                  h="100%"
                />
              </Box>

              {/* Status Text */}
              <VStack spacing={pxToRem(16)} textAlign="center">
                <Heading
                  as="h2"
                  fontSize={pxToRem(28)}
                  fontWeight="bold"
                  color="gray.800"
                >
                  Checking Payment Result
                </Heading>
                
                <Text
                  fontSize={pxToRem(18)}
                  color="gray.600"
                  lineHeight="1.5"
                  maxW={pxToRem(400)}
                >
                  Please wait while we verify your payment status. This may take a few moments.
                </Text>
                
                {statusMessage && (
                  <Text
                    fontSize={pxToRem(16)}
                    color="gray.500"
                    fontStyle="italic"
                  >
                    {statusMessage}
                  </Text>
                )}
              </VStack>
            </VStack>
          </Box>
        </Container>
      </Flex>
    </Box>
  );
};

export default PaymentPendingPage;